import type { Rng } from '../../core/rng/Rng';
import type { RunState } from './RunState';
import type { Player } from '../entities/Player';

/**
 * Entrance shop (floors 2+): guaranteed encounter at spawn, gem-priced,
 * limited stock. Consumables apply immediately and are run-scoped (floor
 * buffs die with the descent; ward/intel are RunState scalars onDescend
 * clears).
 *
 * Reworked 2026-07-27 (Sarah): Great Mend CUT (no full resets, for
 * anyone), small heals up to ×3 but HIDDEN for heal-blocked characters
 * (Vane/Thirl — selling them dead stock was a gem trap), and the stock got
 * interesting: Wardstone (absorb-a-hit — THE defensive buy for the
 * heal-blocked), a seeded utility slot (floor intel / magnet), and a
 * 4-way buff rotation.
 *
 * DRAW CONTRACT: generateShopStock consumes EXACTLY 2 draws (utility pick,
 * buff pick) — heal/ward inclusion is draw-free (healBlock is run-static),
 * so character can never shift a seeded stream.
 *
 * Tuning rule from the design doc: prices scale SLOWER than the gem depth
 * multiplier (×2.5/×3.5), or the descent carrot dies. priceScale is
 * ×1.5/×2.0 on floors 2/3 (steepened 2026-07-28 with the gem-curve reshape —
 * deep floors were awash; the shop.test pin guards the ordering).
 */

export type ShopItemId =
  | 'mend' | 'wardstone' | 'sketch' | 'magnet'
  | 'bloodwhet' | 'quicksilver' | 'oilcloth' | 'lodemark';

export interface ShopItem {
  id: ShopItemId;
  name: string;
  desc: string;
  price: number;
  stock: number;
}

/** Wardstone charges cap — more would trivialize contact pressure. */
export const WARD_CAP = 2;

export function priceScale(floor: number): number {
  return 1 + 0.5 * (floor - 1);
}

export function generateShopStock(
  rng: Rng,
  floor: number,
  healBlock: 'none' | 'all' | 'exceptLeech' = 'none',
): ShopItem[] {
  const s = priceScale(floor);
  // Both seeded picks happen FIRST and ALWAYS — stock composition below
  // (heals by healBlock) is draw-free.
  const utility: ShopItem = rng.bool()
    ? {
      id: 'sketch', name: "Cartographer's Sketch",
      desc: 'reveals this floor on the map',
      price: Math.round(40 * s), stock: 1,
    }
    : {
      id: 'magnet', name: 'Magnet Draught',
      desc: '+40% gem pickup radius this floor',
      price: Math.round(35 * s), stock: 1,
    };
  const buff = rng.pick<ShopItem>([
    {
      id: 'bloodwhet', name: 'Bloodwhet', desc: '+20% damage this floor',
      price: Math.round(55 * s), stock: 1,
    },
    {
      id: 'quicksilver', name: 'Quicksilver', desc: '+15% move speed this floor',
      price: Math.round(45 * s), stock: 1,
    },
    {
      id: 'oilcloth', name: 'Oilcloth', desc: '−10% cooldowns this floor',
      price: Math.round(50 * s), stock: 1,
    },
    {
      id: 'lodemark', name: 'Lodemark', desc: '+15% ability area this floor',
      price: Math.round(45 * s), stock: 1,
    },
  ]);

  const items: ShopItem[] = [];
  if (healBlock === 'none') {
    items.push({
      id: 'mend', name: 'Mend', desc: 'restore 2 HP',
      price: Math.round(35 * s), stock: 3,
    });
  }
  items.push({
    id: 'wardstone', name: 'Wardstone',
    desc: 'absorb the next hit this floor',
    price: Math.round(60 * s), stock: 2,
  });
  items.push(utility, buff);
  return items;
}

/** Returns false if it can't be bought (broke, out of stock, or capped). */
export function purchase(item: ShopItem, runState: RunState, player: Player): boolean {
  if (item.stock <= 0 || runState.gemsHeld < item.price) return false;
  // Ward cap: refuse the sale instead of eating gems for nothing.
  if (item.id === 'wardstone' && runState.wardCharges >= WARD_CAP) return false;
  runState.gemsHeld -= item.price; // gemsEarned untouched — the split matters
  item.stock--;
  switch (item.id) {
    case 'mend':
      player.heal(2);
      break;
    case 'wardstone':
      runState.wardCharges = Math.min(WARD_CAP, runState.wardCharges + 1);
      break;
    case 'sketch':
      runState.floorIntel = true;
      break;
    case 'magnet':
      runState.stats.addModifiers('buff:magnet', [{ stat: 'magnetRange', pct: 0.4 }]);
      runState.floorBuffs.add('buff:magnet');
      break;
    case 'bloodwhet':
      runState.stats.addModifiers('buff:bloodwhet', [{ stat: 'damageMult', pct: 0.2 }]);
      runState.floorBuffs.add('buff:bloodwhet');
      break;
    case 'quicksilver':
      runState.stats.addModifiers('buff:quicksilver', [{ stat: 'moveSpeed', pct: 0.15 }]);
      runState.floorBuffs.add('buff:quicksilver');
      break;
    case 'oilcloth':
      runState.stats.addModifiers('buff:oilcloth', [{ stat: 'cooldownMult', pct: -0.1 }]);
      runState.floorBuffs.add('buff:oilcloth');
      break;
    case 'lodemark':
      runState.stats.addModifiers('buff:lodemark', [{ stat: 'areaMult', pct: 0.15 }]);
      runState.floorBuffs.add('buff:lodemark');
      break;
  }
  return true;
}
